import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedNumber } from 'react-intl';

import { Alert as AlertAntd, App, Button, Checkbox, Modal, Spin, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import Alert from 'components/Alert';
import CustomMaskedInput from 'components/MaskedInput';
import ModalActions from 'components/ModalActions';
import Truncate from 'components/Truncate';
import { agent, functions } from 'helpers';
import {
  PF_TRANSFER_LIMIT,
  FINANCIAL_BLOCK_TOOLTIP_MESSAGE,
  MIN_TRANSFER_INTERNATIONAL,
  MIN_TRANSFER
} from 'helpers/constants';
import { HotjarEvents, submitEvent } from 'hotjar';
import type IAnticipation from 'interfaces/IAnticipation';
import { EAnticipationStatus } from 'interfaces/IUser';
import { useBalance } from 'scenes/Producer/Dashboard/components/Wallet/useBalance';
import { API_MYEDUZZ } from 'settings';
import InstallmentCashbackAlert from 'v2/components/Pages/FinancialStatement/CardList/AnticipationCard/InstallmentCashbackAlert';
import WithBetaModules from 'v2/components/Shared/WithBetaModules';
import { numberToBRL } from 'v2/helpers/money';
import { useProfileListAccounts } from 'v2/hooks/useProfileListAccounts';
import { npsService } from 'v2/services/nps';
import { useUserStore, useUserUpdate } from 'v2/store/User';

import { Icon } from '@eduzz/ui-antd-theme';

import AccountList from './components/AccountList';
import { useBankTransfer } from './useBankTransfer';
import { useListInvoiceAntecipation } from './useListInvoiceAntecipation';
import { useListTransfers } from './useListTransfers';

const TRANSFER_FEEDBACK_SURVEY_ID = 796843;
const TRANSFER_FEEDBACK_SURVEY_EXPIRATION_IN_DAYS = 90;
const TRANSFER_FEE = 9;
const TOOLTIP_MESSAGE_COMMISSION_UNMARKABLE =
  'Essa comissão já está selecionada e não pode ser desmarcada. O produto associado não está disponível para antecipação de recebimento por parcelas.';
const TOOLTIP_MESSAGE_INVOICE_UNSELECTABLE =
  'Não é possível selecionar essa fatura devido o valor á antecipar ser maior que o permitido';

interface IProps {
  currency?: any;
  open: boolean;
  onCloseModalTransfer: any;
  successCallback?: (...args: any[]) => any;
  selfClose: (...args: any[]) => any;
  transferByInvoice: boolean;
  newAntecipationTransfer: boolean;
}

const parseFieldToFloat = (fieldValue: string | number): number => {
  return Number.parseFloat(functions.clearMoneyMask(fieldValue));
};

const getNewAnticipationSelectedValue = (newInvoices: IAnticipation[]): number => {
  const allCommissionsSelected = newInvoices.flatMap(invoice =>
    invoice.commissions.filter(commission => commission.selected)
  );
  const allSelectedGains = allCommissionsSelected.map(commission => commission.gain);
  return allSelectedGains.reduce((currentGain, total) => currentGain + total, 0);
};

const Transaction = ({ open, onCloseModalTransfer, selfClose, successCallback }: IProps) => {
  const { message } = App.useApp();
  const [valueTransfer, setValueTransfer] = useState<any>(0);
  const [modalTransfers, setModalTransfers] = useState(false);
  const [checkedAllAnticipation, setCheckedAllAnticipation] = useState(false);
  const [invoiceTable, setInvoiceTable] = useState(false);
  const [availableValue, setAvailableValue] = useState<any>(0);
  const [arrayInvoicesAnticipation, setArrayInvoicesAnticipation] = useState<IAnticipation[]>([]);
  const [selectedAnticipationValues, setSelectedAnticipationValues] = useState(0);
  const [changeAccountList, setChangeAccountList] = useState(false);
  const [accountActive, setAccountActive] = useState<any>({});
  const [showAccount, setShowAccount] = useState(false);
  const [isLoadingAnticipation, setIsLoadingAnticipation] = useState(true);
  const [isLoadingAnticipationInvoices, setIsLoadingAnticipationInvoices] = useState(true);
  const [isLoadingTransferByInvoice, setIsLoadingTransferByInvoice] = useState(false);
  const [anticipationAccessStatus, setAnticipationAccessStatus] = useState('blocked');
  const { data: listAccounts, isLoading: listAccountsIsLoading } = useProfileListAccounts(true);

  const { originalUser } = useUserStore(({ originalUser }) => ({ originalUser }));

  const { data: balance, isFetching: isLoadingBalance } = useBalance();
  const {
    data: { invoices: listAnticipations, availableAnticipation },
    isFetching: anticipationsIsFetching
  } = useListInvoiceAntecipation();

  const { data: listTransfers, isFetching: isLoadingTransfer } = useListTransfers();

  const { authUser, transferByInvoice, newAntecipationTransfer } = useUserStore(({ user }) => ({
    authUser: user,
    transferByInvoice: user.invoice_transfer,
    newAntecipationTransfer: user.new_anticipation_transfer
  }));

  const selectedAnticipationValuesRef = useRef(selectedAnticipationValues);
  selectedAnticipationValuesRef.current = selectedAnticipationValues;

  const arrayInvoicesAnticipationRef = useRef(arrayInvoicesAnticipation);
  arrayInvoicesAnticipationRef.current = arrayInvoicesAnticipation;

  const accountActiveRef = useRef(accountActive);
  accountActiveRef.current = accountActive;

  const availableValueRef = useRef(availableValue);
  availableValueRef.current = availableValue;

  const valueTransferRef = useRef(valueTransfer);
  valueTransferRef.current = valueTransfer;

  const { updateUser } = useUserUpdate();
  const { bankTransfer, isPending: isPendingBankTransfer } = useBankTransfer();

  const hasAvailableValue = useCallback(() => {
    return !isLoadingBalance && balance?.value > 0;
  }, [balance?.value, isLoadingBalance]);

  const hasSelectedAnticipation = useCallback(() => {
    return selectedAnticipationValuesRef.current > 0;
  }, []);

  const getActualAmountToTransferWithoutNegative = useCallback((): number => {
    const currentSelectedAnticipation = selectedAnticipationValuesRef.current;
    const currentValueTransfer = valueTransferRef.current;

    if (!hasSelectedAnticipation() && hasAvailableValue()) {
      return parseFieldToFloat(currentValueTransfer);
    }

    return currentSelectedAnticipation + parseFieldToFloat(currentValueTransfer);
  }, [hasSelectedAnticipation, hasAvailableValue]);

  const getActualAmountToTransfer = useCallback((): number => {
    if (balance?.value >= 0) {
      return getActualAmountToTransferWithoutNegative();
    }

    const value = balance.value > 0 ? parseFieldToFloat(balance?.value) : balance?.value;
    return getActualAmountToTransferWithoutNegative() + value;
  }, [balance?.value, getActualAmountToTransferWithoutNegative]);

  const handleClickTransfers = useCallback(
    (value: number) => {
      const currentArrayInvoices = arrayInvoicesAnticipationRef.current;

      const minToTransfer = authUser?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;
      const valueAvailable = balance?.value;
      const account = listAccounts.length ? accountActiveRef.current.id : null;

      const commissionsSelected = currentArrayInvoices.flatMap(invoice =>
        invoice.commissions.filter(commission => commission.selected)
      );
      const arrayIds = commissionsSelected.map(commission => commission.id);
      const totalAnticipation = commissionsSelected
        .map(commission => commission.gain)
        .reduce((accumulator, item) => accumulator + item, 0);

      setModalTransfers(false);

      let success = true;

      if (!account) {
        success = false;
        message.error('Por favor cadastre uma conta bancária.');
      }

      if (getActualAmountToTransfer() <= 0) {
        success = false;
        message.error('Por favor digite um valor válido para a transferência.');
      }

      if (valueAvailable > 0 && value > valueAvailable) {
        success = false;
        message.error('O valor da transferência é maior que o saldo disponível.');
      }

      if (totalAnticipation < 0) {
        success = false;
        message.error('Por favor digite um valor válido para a antecipação.');
      }

      if (Math.trunc(totalAnticipation * 100) / 100 > Math.trunc(balance?.available_antecipation * 100) / 100) {
        success = false;
        message.error('O valor da antecipação é maior que o saldo disponível.');
      }

      if (valueAvailable > 0 && value < valueAvailable && totalAnticipation > 0) {
        success = false;
        message.error('Por favor utilize 100% do valor de transferencia para solicitar antecipação!');
      }

      if (getActualAmountToTransfer() < minToTransfer) {
        success = false;
        message.error(`O valor da transferência é menor que R$ ${minToTransfer}.`);
      }

      if (success) {
        (async () => {
          try {
            const actualAmountToTransfer = getActualAmountToTransfer();

            if (actualAmountToTransfer > 0) {
              await bankTransfer({
                value: actualAmountToTransfer,
                transfer_method_id: account,
                commissions: arrayIds
              });
            }

            selfClose();

            if (successCallback) {
              await successCallback(true);
            }

            message.success('Sucesso, transferência enviada para processamento com êxito!');
            await updateUser();

            if (successCallback) {
              await successCallback(false);
            }

            const lastAnswer = await npsService.getSurveyLastAnswer(TRANSFER_FEEDBACK_SURVEY_ID);

            if (lastAnswer) {
              const isSurveyExpired =
                lastAnswer.answered && lastAnswer.days_count >= TRANSFER_FEEDBACK_SURVEY_EXPIRATION_IN_DAYS;
              const shouldShowSurvey = !lastAnswer.answered || isSurveyExpired;

              if (shouldShowSurvey) {
                submitEvent(HotjarEvents.TRANSFER_REQUEST_MADE);
              }
            }
          } catch (err) {
            message.error(typeof err === 'string' ? err : 'Erro ao processar transferência.');
          }
        })();
      }
    },
    [
      authUser,
      balance,
      listAccounts,
      updateUser,
      selfClose,
      bankTransfer,
      successCallback,
      getActualAmountToTransfer,
      message
    ]
  );

  const onOpenModalTransfers = useCallback(
    async (value: number) => {
      const amount = Number(functions.clearMoneyMask(availableValueRef.current));

      if (transferByInvoice) {
        if (amount > balance?.value) {
          message.warning('Ops, Valor solicitado para transferência é maior que o disponível');
          return;
        }

        let transf: any = null;

        try {
          setIsLoadingTransferByInvoice(true);
          const minToTransfer = authUser?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;
          const data: any = await agent.post(`${API_MYEDUZZ}/transfers/check_amount_invoices`, { amount });
          transf = data.data.data;

          if (transf.approx_amount < minToTransfer) {
            await Alert.show({
              message: `O valor mínimo para saque em nossa plataforma é de ${numberToBRL(
                minToTransfer
              )} e a soma de faturas com valor mais próximo ao solicitado é de ${
                transf.approx_amount
              }. Tente novamente com um valor acima disso e dará tudo certo ;)`,
              title: 'Ops, foi por pouco',
              confirmation: false
            });
            setIsLoadingTransferByInvoice(false);
            return;
          }

          const confirm = await Alert.show({
            message: `A soma de faturas com valor mais próximo ao solicitado é de ${transf.approx_amount}. Deseja continuar com a transferência?`,
            title: 'Valor à Transferir',
            confirmation: true
          });

          if (!confirm) {
            setIsLoadingTransferByInvoice(false);
            return;
          }
        } catch (error) {
          setIsLoadingTransferByInvoice(false);
          message.error(error?.data?.message || 'Erro ao validar transferência.');
        }

        try {
          if (transf) {
            await agent.post(`${API_MYEDUZZ}/transfers/amount_invoices`, {
              transfer_key: transf.transfer_key,
              transfer_method_id: accountActiveRef.current.id
            });
            setIsLoadingTransferByInvoice(false);
            message.success('Sucesso, transferência enviada para processamento com êxito!');
            selfClose();
            await updateUser();

            if (successCallback) {
              await successCallback(false);
            }
          }
        } catch (error) {
          message.error(error?.data?.message || 'Erro ao processar transferência.');
          setIsLoadingTransferByInvoice(false);
        }
      } else {
        setModalTransfers(true);
        setValueTransfer(value);
      }
    },
    [transferByInvoice, balance, authUser, updateUser, selfClose, successCallback, message]
  );

  const onCancelModalTransfers = useCallback(() => {
    setModalTransfers(false);
  }, []);

  const handleChangeTransferInput = useCallback((value: string | number) => {
    setAvailableValue(value);
    setValueTransfer(value);
  }, []);

  const handleClickExpandedInvoices = useCallback(() => {
    setInvoiceTable(prev => !prev);
  }, []);

  const handleChangeSelectCommission = useCallback((item: IAnticipation, selectedCommision: number) => {
    setArrayInvoicesAnticipation(prevInvoices => {
      const currentInvoice = { ...item, commissions: item.commissions.map(c => ({ ...c })) };
      const currentCommission = currentInvoice.commissions.find(commission => commission.id === selectedCommision);
      const isSelected = !currentCommission.selected;

      if (!currentCommission.isSelectable) {
        return prevInvoices;
      }

      currentInvoice.commissions.find(commission => commission.id === selectedCommision).selected = isSelected;
      currentInvoice.commissions.forEach(commission => {
        if (isSelected && !commission.isSelectable) {
          commission.selected = isSelected;
        }
      });

      const commissionsSelected = currentInvoice.commissions.filter(commission => commission.selected).length;
      currentInvoice.selected = commissionsSelected === currentInvoice.commissions.length;

      const newInvoices = prevInvoices.map((invoice: IAnticipation) => {
        if (invoice.id === currentInvoice.id) {
          return currentInvoice;
        }

        return invoice;
      });

      const hasCommissionsUnchecked = !!newInvoices.filter(
        invoice => invoice.commissions.filter(commission => !commission.selected).length
      ).length;

      setSelectedAnticipationValues(getNewAnticipationSelectedValue(newInvoices));
      setCheckedAllAnticipation(!hasCommissionsUnchecked);

      return newInvoices;
    });
  }, []);

  const handleChangeSelectInvoice = useCallback((item: IAnticipation) => {
    setArrayInvoicesAnticipation(prevInvoices => {
      const currentInvoice = { ...item, commissions: item.commissions.map(c => ({ ...c })) };
      const isSelected = !currentInvoice.selected;

      currentInvoice.selected = isSelected;
      currentInvoice.commissions.forEach(commission => {
        commission.selected = isSelected;
      });

      const newInvoices = prevInvoices.map((invoice: IAnticipation) => {
        if (invoice.id === currentInvoice.id) {
          return currentInvoice;
        }

        return invoice;
      });

      const hasCommissionsUnchecked = !!newInvoices.filter(
        invoice => invoice.commissions.filter(commission => !commission.selected).length
      ).length;

      setSelectedAnticipationValues(getNewAnticipationSelectedValue(newInvoices));
      setCheckedAllAnticipation(!hasCommissionsUnchecked);

      return newInvoices;
    });
  }, []);

  const handleChangeSelectAll = useCallback((event: any) => {
    event.stopPropagation();
    const checked = event.target.checked;

    setArrayInvoicesAnticipation(prevInvoices => {
      const newInvoices = prevInvoices.map(invoice => ({
        ...invoice,
        selected: checked,
        commissions: invoice.commissions.map(commission => ({ ...commission, selected: checked }))
      }));

      setSelectedAnticipationValues(getNewAnticipationSelectedValue(newInvoices));
      setCheckedAllAnticipation(checked);

      return newInvoices;
    });
  }, []);

  const handleChangeAccount = useCallback((account: any) => {
    setAccountActive(account);
    setChangeAccountList(prev => !prev);
  }, []);

  const toggleChangeAccount = useCallback(() => {
    setChangeAccountList(prev => !prev);
  }, []);

  const showBankAccount = useCallback((status: boolean) => {
    setShowAccount(status);
  }, []);

  const isInvoiceSelected = useCallback(
    (invoice: IAnticipation) => {
      const alreadyTransfered = listTransfers?.already_transfered;
      const limitPF = PF_TRANSFER_LIMIT - alreadyTransfered;

      if (authUser.type === 'F' && selectedAnticipationValues + invoice.gain > limitPF) {
        return true;
      }

      return !invoice.selected && balance?.available_antecipation < selectedAnticipationValues + invoice.gain;
    },
    [authUser, listTransfers, balance, selectedAnticipationValues]
  );

  const canAnticipateResult = useMemo(() => {
    const alreadyTransfered = listTransfers?.already_transfered;

    if (originalUser?.anticipation_status === EAnticipationStatus.BLOQUEADO) {
      return {
        anticipationUnavailableMessage: 'Antecipação bloqueada por alto índice de chargeback',
        canAnticipate: false
      };
    }

    if (anticipationAccessStatus === 'blocked') {
      return {
        anticipationUnavailableMessage:
          'Ainda não é possível antecipar suas vendas. Para utilizar essa funcionalidade, aguarde o prazo de 30 dias a partir da data de pagamento da sua primeira venda.',
        canAnticipate: false
      };
    }

    if (authUser.type === 'F' && alreadyTransfered >= PF_TRANSFER_LIMIT) {
      return {
        anticipationUnavailableMessage:
          'A antecipação irá ultrapassar o limite disponível para transferência (R$1.900,00)',
        canAnticipate: false
      };
    }

    if (Array.isArray(arrayInvoicesAnticipation) && arrayInvoicesAnticipation.length <= 0) {
      return {
        anticipationUnavailableMessage: 'Você não possui faturas para antecipar',
        canAnticipate: false
      };
    }

    const transferValue = Number.parseFloat(functions.clearMoneyMask(selectedAnticipationValues));
    const valueAvailableToAnticipate = Number.parseFloat(functions.clearMoneyMask(balance?.available_antecipation));

    if (transferValue > valueAvailableToAnticipate) {
      return {
        anticipationUnavailableMessage: 'Valor indisponível',
        canAnticipate: false
      };
    }

    if (valueAvailableToAnticipate === 0) {
      return {
        anticipationUnavailableMessage: 'Você não possui saldo para antecipar',
        canAnticipate: false
      };
    }

    if (transferByInvoice || !newAntecipationTransfer) {
      return {
        anticipationUnavailableMessage: 'Antecipação indisponível',
        canAnticipate: false
      };
    }

    return {
      anticipationUnavailableMessage: '',
      canAnticipate: true
    };
  }, [
    listTransfers,
    originalUser,
    anticipationAccessStatus,
    authUser,
    arrayInvoicesAnticipation,
    selectedAnticipationValues,
    balance,
    transferByInvoice,
    newAntecipationTransfer
  ]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const featureAccessResult = await agent.get(`${API_MYEDUZZ}/customers/feature-access`);

      if (cancelled) {
        return;
      }

      const featureAnticipationStatus = featureAccessResult.data.data.anticipation.status;
      setAnticipationAccessStatus(featureAnticipationStatus);
      setIsLoadingAnticipation(false);
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [listTransfers]);

  useEffect(() => {
    if (listAnticipations) {
      const newInvoices: Array<IAnticipation> = listAnticipations;
      setArrayInvoicesAnticipation(newInvoices);
      setSelectedAnticipationValues(getNewAnticipationSelectedValue(newInvoices));
      setIsLoadingAnticipationInvoices(false);
    }
  }, [listAnticipations]);

  useEffect(() => {
    if (!isLoadingAnticipation && listAccounts && !listAccountsIsLoading) {
      const getAccount = () => {
        if (!listAccounts?.length) {
          return null;
        }

        const findAccount = listAccounts?.find((item: any) => item.main);

        if (!findAccount) {
          return listAccounts[0];
        }

        return findAccount;
      };

      setAccountActive(getAccount());
      setShowAccount(false);
    }
  }, [isLoadingAnticipation, listAccounts, listAccountsIsLoading]);

  const minToTransfer = authUser?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;

  const hasSomeCommissionChecked = !!arrayInvoicesAnticipation.filter(
    invoice => invoice.commissions.filter(commission => commission.selected).length
  ).length;

  const { canAnticipate, anticipationUnavailableMessage } = canAnticipateResult;

  const cantTransfer = useMemo(() => {
    if (isPendingBankTransfer) {
      return true;
    }

    if (anticipationsIsFetching) {
      return true;
    }

    const hasAnticipation = Number.parseFloat(selectedAnticipationValues.toString()) > 0;
    const hasTransfer = Number.parseFloat(functions.clearMoneyMask(valueTransfer)) > 0;
    const transferValue = Number.parseFloat(functions.clearMoneyMask(valueTransfer));
    const transferAvailable = balance ? balance?.value : 0;
    const availableValueFloat = Number.parseFloat(functions.clearMoneyMask(availableValue));

    if (availableValueFloat < transferAvailable && hasAnticipation) {
      return true;
    }

    if (getActualAmountToTransfer() < minToTransfer) {
      return true;
    }

    if (hasAnticipation && hasTransfer && transferValue !== transferAvailable) {
      return true;
    }

    if (
      !hasAnticipation &&
      hasTransfer &&
      getActualAmountToTransfer() > Number.parseFloat(functions.clearMoneyMask(transferAvailable))
    ) {
      return true;
    }

    return false;
  }, [
    anticipationsIsFetching,
    isPendingBankTransfer,
    selectedAnticipationValues,
    valueTransfer,
    balance,
    availableValue,
    minToTransfer,
    getActualAmountToTransfer
  ]);

  const renderTooltipValue = useMemo(() => {
    const availableValueFloat = Number.parseFloat(functions.clearMoneyMask(availableValue));

    if (availableValueFloat < balance?.value && checkedAllAnticipation) {
      return <p>Use todo o seu saldo disponível antes de antecipar</p>;
    }

    if (getActualAmountToTransfer() < minToTransfer) {
      return (
        <p>
          Valor mínimo para transferir: <FormattedNumber value={minToTransfer} format={'BRL'}></FormattedNumber>
        </p>
      );
    }

    return '';
  }, [availableValue, balance, checkedAllAnticipation, minToTransfer, getActualAmountToTransfer]);

  const anticipationColumns: ColumnsType<IAnticipation> = useMemo(
    () => [
      {
        title: '*',
        key: 'select',
        width: 40,
        render: (_: unknown, invoice: IAnticipation) => {
          const isNotSelected = !invoice.selected && isInvoiceSelected(invoice);
          const commissionsSelected = invoice.commissions.filter(commission => commission.selected).length;
          const indeterminate = commissionsSelected > 0 && commissionsSelected < invoice.commissions.length;

          return (
            <Tooltip placement='bottom' title={isNotSelected ? TOOLTIP_MESSAGE_INVOICE_UNSELECTABLE : ''}>
              <span>
                <Checkbox
                  checked={invoice.selected}
                  disabled={isNotSelected}
                  indeterminate={indeterminate}
                  onChange={() => handleChangeSelectInvoice(invoice)}
                />
              </span>
            </Tooltip>
          );
        }
      },
      {
        title: 'Descrição',
        key: 'description',
        render: (_: unknown, invoice: IAnticipation) => (
          <span>
            <Truncate className='inline' count={25} final={'...'}>
              {invoice.description}
            </Truncate>
            {` - FAT ${invoice.id}`}
          </span>
        )
      },
      {
        title: 'Valor líquido',
        dataIndex: 'gain',
        key: 'gain',
        align: 'right',
        width: 120,
        render: (value: number) => <FormattedNumber value={value} format='BRL' />
      }
    ],
    [isInvoiceSelected, handleChangeSelectInvoice]
  );

  return (
    <>
      <Modal
        open={open}
        onCancel={onCloseModalTransfer}
        centered
        width={650}
        title={
          !changeAccountList ? 'Informe o valor que deseja transferir' : 'Selecione a conta bancária para transferir'
        }
        footer={
          !changeAccountList ? (
            <div className='flex justify-end'>
              <Button type='text' onClick={onCloseModalTransfer}>
                Cancelar
              </Button>
            </div>
          ) : null
        }
      >
        <div className='flex max-h-[80vh] flex-col gap-4 overflow-auto'>
          {!changeAccountList && (
            <>
              <div className='rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
                <div className='flex w-full flex-wrap items-center justify-between gap-3'>
                  <Typography.Text>Saldo Disponível:</Typography.Text>
                  <Typography.Text strong>
                    {balance && (balance?.value || balance?.value === 0) ? (
                      <FormattedNumber value={balance?.value} format='BRL' />
                    ) : (
                      'Carregando...'
                    )}
                  </Typography.Text>
                  <div className='w-full sm:max-w-55'>
                    <CustomMaskedInput
                      id='input-available'
                      className='m-0 w-full'
                      mask='money'
                      onChange={(value: number | string) => handleChangeTransferInput(value)}
                      disabled={!hasAvailableValue()}
                      margin='normal'
                      disableUnderline={true}
                      value={functions.addMoneyMask(availableValue.toString())}
                    />
                  </div>
                </div>
              </div>

              {!newAntecipationTransfer && (
                <AlertAntd
                  type='warning'
                  showIcon
                  message='Antecipação manual desabilitada'
                  description={
                    <>
                      Se você necessita antecipar algum valor, abra um chamado com o título &quot;Antecipação&quot; em{' '}
                      <Typography.Text strong>suporte@eduzz.com</Typography.Text>. As antecipações solicitadas serão
                      realizadas às 12h00 e 17h00 de segunda à sexta.
                    </>
                  }
                />
              )}

              <div className='border-outline-default w-full border-b py-2 max-sm:px-2 max-sm:py-4 max-sm:pb-6 max-sm:text-right'>
                <div className='flex w-full items-center justify-between text-base max-sm:flex-wrap'>
                  {isLoadingTransfer || isLoadingAnticipation || isLoadingAnticipationInvoices ? (
                    <div>
                      <span>Carregando...</span>
                    </div>
                  ) : (
                    <>
                      {canAnticipate && (
                        <>
                          <div className={'amount__checkbox'}>
                            <Checkbox
                              checked={checkedAllAnticipation}
                              onChange={handleChangeSelectAll}
                              className='p-1'
                              indeterminate={!!(hasSomeCommissionChecked && !checkedAllAnticipation)}
                            />
                            <span className='label ml-2.5'>Faturas Antecipáveis:</span>
                          </div>
                          <div className={'amount__value'}>
                            <span className='cursor-pointer' onClick={handleClickExpandedInvoices}>
                              <FormattedNumber value={availableAnticipation} format='BRL' />
                            </span>
                            <span
                              className='relative ml-1 cursor-pointer transition-all duration-300'
                              onClick={handleClickExpandedInvoices}
                            >
                              {invoiceTable ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
                            </span>
                          </div>
                          <div className={'amount__input'}>
                            <CustomMaskedInput
                              id='input-anticipation'
                              className='m-0 w-full'
                              mask='money'
                              margin='normal'
                              disabled
                              disableUnderline={true}
                              value={functions.addMoneyMask(selectedAnticipationValues.toString())}
                            />
                          </div>
                        </>
                      )}
                      {!canAnticipate && (
                        <AlertAntd type='error' className='w-full' showIcon message={anticipationUnavailableMessage} />
                      )}
                    </>
                  )}
                </div>
              </div>

              {newAntecipationTransfer && <InstallmentCashbackAlert enabled={open} />}

              {invoiceTable && (
                <div className='border-outline-default mt-2 max-h-57.5 w-full overflow-x-auto rounded border'>
                  {anticipationsIsFetching && (
                    <div className='flex w-full justify-center py-4'>
                      <Spin size='small' />
                    </div>
                  )}
                  {!anticipationsIsFetching && (
                    <Table<IAnticipation>
                      size='small'
                      rowKey={invoice => String(invoice.id)}
                      columns={anticipationColumns}
                      dataSource={listAnticipations ? arrayInvoicesAnticipation : []}
                      pagination={false}
                      locale={{ emptyText: 'Nenhuma fatura encontrada...' }}
                      expandable={{
                        showExpandColumn: arrayInvoicesAnticipation.some(invoice => invoice.commissions.length > 1),
                        rowExpandable: invoice => invoice.commissions.length > 1,
                        expandedRowRender: invoice => (
                          <div className='flex flex-col gap-1'>
                            {invoice.commissions.map(commission => (
                              <div key={commission.id} className='flex w-full items-center'>
                                <Tooltip
                                  title={
                                    !!commission.selected && !commission.isSelectable
                                      ? TOOLTIP_MESSAGE_COMMISSION_UNMARKABLE
                                      : ''
                                  }
                                >
                                  <span>
                                    <Checkbox
                                      checked={!!commission.selected}
                                      onChange={() => handleChangeSelectCommission(invoice, commission.id)}
                                    />
                                  </span>
                                </Tooltip>
                                <span className='ml-2 flex-1'>{`${commission.id} - ${commission.description}`}</span>
                                <span className='text-right'>
                                  <FormattedNumber value={commission.gain} format='BRL' />
                                </span>
                              </div>
                            ))}
                          </div>
                        )
                      }}
                    />
                  )}
                </div>
              )}
              <div className='flex w-full items-center justify-between rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
                <Typography.Text strong>Valor a transferir:</Typography.Text>
                <Typography.Text strong>
                  <FormattedNumber value={getActualAmountToTransfer()} format='BRL' />
                </Typography.Text>
              </div>
              <div className='flex w-full flex-col gap-1'>
                {listAccounts && !listAccountsIsLoading && listAccounts.length > 0 && (
                  <div className='flex flex-col gap-1'>
                    <Typography.Text>
                      Transferir para Banco:{' '}
                      {showAccount ? (
                        <>
                          <Typography.Text strong>{accountActive.bank_id}</Typography.Text>, Ag:{' '}
                          <Typography.Text strong>
                            {accountActive.agency}-{accountActive.agency_digit}
                          </Typography.Text>
                          , Conta:{' '}
                          <Typography.Text strong>
                            {accountActive.account}-{accountActive.account_digit}
                          </Typography.Text>
                          , <Typography.Text strong>{accountActive.type}</Typography.Text>
                          <Button
                            shape='circle'
                            type='text'
                            icon={<Icon.EyeOff />}
                            onClick={() => showBankAccount(false)}
                          />
                        </>
                      ) : (
                        <>
                          <Typography.Text strong>
                            {accountActive.bank_id && accountActive.bank_id.replace(/[0-9]/g, '*')}
                          </Typography.Text>
                          , Ag:{' '}
                          <Typography.Text strong>
                            {accountActive.agency && accountActive.agency.replace(/[0-9]/g, '*')}-
                            {accountActive.agency_digit && accountActive.agency_digit.replace(/[0-9]/g, '*')}
                          </Typography.Text>
                          , Conta:{' '}
                          <Typography.Text strong>
                            {accountActive.account && accountActive.account.replace(/[0-9]/g, '*')}-
                            {accountActive.account_digit && accountActive.account_digit.replace(/[0-9]/g, '*')}
                          </Typography.Text>
                          .
                          <Button
                            shape='circle'
                            type='text'
                            icon={<Icon.Eye />}
                            onClick={() => showBankAccount(true)}
                          />
                        </>
                      )}
                    </Typography.Text>
                    <Typography.Link onClick={toggleChangeAccount}>Clique aqui para alterar a conta</Typography.Link>
                  </div>
                )}
                {listAccounts && listAccountsIsLoading && (
                  <div className='flex w-full justify-center'>
                    <Spin size='small' />
                  </div>
                )}
                {listAccounts && !listAccountsIsLoading && listAccounts.length === 0 && (
                  <Typography.Text>Nenhuma conta bancária está ativa.</Typography.Text>
                )}
              </div>
              <WithBetaModules>
                {(betaModules: string[]) => {
                  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
                  const renderTooltipBlockTransfer = userHasBlockTransferBetaModule
                    ? FINANCIAL_BLOCK_TOOLTIP_MESSAGE
                    : false;
                  return (
                    <Tooltip title={renderTooltipBlockTransfer || renderTooltipValue} placement='top'>
                      <div className='w-full'>
                        <Button
                          type='primary'
                          block
                          onClick={() =>
                            onOpenModalTransfers(
                              availableValue ? Number.parseFloat(functions.clearMoneyMask(availableValue)) : 0
                            )
                          }
                          disabled={cantTransfer || isLoadingTransferByInvoice || userHasBlockTransferBetaModule}
                          loading={isPendingBankTransfer || anticipationsIsFetching || isLoadingTransferByInvoice}
                        >
                          Transferir
                        </Button>
                      </div>
                    </Tooltip>
                  );
                }}
              </WithBetaModules>
            </>
          )}

          {changeAccountList && (
            <AccountList
              handleChangeAccount={handleChangeAccount}
              accountDefault={accountActive}
              listAccounts={listAccounts}
              toggleChangeAccount={toggleChangeAccount}
            />
          )}
        </div>
      </Modal>
      <ModalActions
        open={modalTransfers}
        onClose={onCancelModalTransfers}
        onOk={() =>
          handleClickTransfers(availableValue ? Number.parseFloat(functions.clearMoneyMask(availableValue)) : 0)
        }
        title={'Atenção'}
        buttonOk={'Continuar'}
        loading={false}
      >
        <p>
          {!hasAvailableValue() ? (
            <p>
              Você solicitou uma transferência com antecipação no valor de{' '}
              <b>
                <FormattedNumber value={getActualAmountToTransferWithoutNegative()} format='BRL' />
              </b>
              , porém o valor disponível para saque atualmente é negativo, portanto, esse valor negativo de <br></br>
              <b>
                <FormattedNumber value={balance?.value} format='BRL' />
              </b>{' '}
              será descontado de sua transferência juntamente com os encargos de transferência no valor de{' '}
              <b>R$ {TRANSFER_FEE},00</b>, resultando no valor final de{' '}
              <b>
                <FormattedNumber value={getActualAmountToTransfer() - TRANSFER_FEE} format='BRL' />
              </b>
              .
            </p>
          ) : (
            <p>
              Você solicitou uma transferência no valor de{' '}
              <b>
                <FormattedNumber value={getActualAmountToTransfer()} format='BRL' />
              </b>
              .
              <br />
              Neste valor, serão descontados os encargos de transferência no valor de R$ {TRANSFER_FEE},00. Então o
              valor total transferido para sua conta será de{' '}
              <b>
                <FormattedNumber value={getActualAmountToTransfer() - TRANSFER_FEE} format='BRL' />
              </b>
              .
            </p>
          )}
          <br />
          Deseja continuar com a transação?
        </p>
      </ModalActions>
    </>
  );
};

export default Transaction;
